/**
 * Pure path-history follower logic (no Phaser). A follower replays the leader's trail at a fixed
 * distance behind, classic party style: it walks exactly where the leader walked, so corners are
 * turned rather than cut and collision does not need to be re-evaluated.
 */
import type { Facing } from '@/core/types';

interface TrailPoint { x: number; y: number; d: number }

export class PathTrail {
  private pts: TrailPoint[] = [];
  constructor(private readonly minStep = 0.5, private readonly keepDistance = 512) {}

  reset(x: number, y: number): void { this.pts = [{ x, y, d: 0 }]; }

  get headDistance(): number { const h = this.pts[this.pts.length - 1]; return h ? h.d : 0; }
  get length(): number { return this.pts.length; }

  /** Records the leader position; ignores jitter smaller than minStep. Returns true when appended. */
  push(x: number, y: number): boolean {
    const h = this.pts[this.pts.length - 1];
    if (!h) { this.reset(x, y); return true; }
    const dist = Math.hypot(x - h.x, y - h.y);
    if (dist < this.minStep) return false;
    this.pts.push({ x, y, d: h.d + dist });
    const cutoff = h.d + dist - this.keepDistance;
    if ((this.pts[0] as TrailPoint).d < cutoff - 64) {
      let i = 0;
      while (i < this.pts.length - 2 && (this.pts[i + 1] as TrailPoint).d < cutoff) i++;
      if (i > 0) this.pts.splice(0, i);
    }
    return true;
  }

  /** Point `behind` px behind the head along the trail, or null when the trail is not long enough. */
  sample(behind: number): { x: number; y: number } | null {
    const h = this.pts[this.pts.length - 1];
    if (!h) return null;
    const target = h.d - behind;
    const first = this.pts[0] as TrailPoint;
    if (target < first.d) return null; // trail shorter than the requested distance
    if (target === first.d) return { x: first.x, y: first.y };
    // binary search for the segment containing `target`
    let lo = 0, hi = this.pts.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((this.pts[mid] as TrailPoint).d <= target) lo = mid; else hi = mid;
    }
    const a = this.pts[lo] as TrailPoint, b = this.pts[hi] as TrailPoint;
    const span = b.d - a.d;
    const t = span > 0 ? (target - a.d) / span : 0;
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
}

export interface FollowOptions {
  /** Distance kept behind the leader along the trail, in px. */
  distance: number;
  /** Follower top speed in px/s (should be >= leader speed so it can catch up). */
  speed: number;
  /** Leader displacement above this in a single step is treated as a teleport. */
  teleportPx?: number;
}

export interface FollowOutput { x: number; y: number; moving: boolean; facing: Facing | null }

/** Step-driven brain: feed it the leader position and its own position every fixed step. */
export class TrailFollowLogic {
  readonly trail = new PathTrail();
  private lastLeader: { x: number; y: number } | null = null;
  constructor(readonly opts: FollowOptions) {}

  reset(leaderX: number, leaderY: number): void { this.trail.reset(leaderX, leaderY); this.lastLeader = { x: leaderX, y: leaderY }; }

  /** Seeds a straight trail segment behind the leader so a fresh follower has somewhere to stand. */
  seed(leaderX: number, leaderY: number, facing: Facing, behind = this.opts.distance): { x: number; y: number } {
    const dx = facing === 'left' ? 1 : facing === 'right' ? -1 : 0;
    const dy = facing === 'up' ? 1 : facing === 'down' ? -1 : 0;
    const sx = leaderX + dx * behind, sy = leaderY + dy * behind;
    this.trail.reset(sx, sy);
    this.trail.push(leaderX, leaderY);
    this.lastLeader = { x: leaderX, y: leaderY };
    return { x: sx, y: sy };
  }

  update(leaderX: number, leaderY: number, selfX: number, selfY: number, dtMs: number): FollowOutput {
    const teleport = this.opts.teleportPx ?? 96;
    if (!this.lastLeader || Math.hypot(leaderX - this.lastLeader.x, leaderY - this.lastLeader.y) > teleport) {
      this.reset(leaderX, leaderY);
      return { x: leaderX, y: leaderY, moving: false, facing: null };
    }
    this.trail.push(leaderX, leaderY);
    this.lastLeader = { x: leaderX, y: leaderY };
    const target = this.trail.sample(this.opts.distance);
    if (!target) return { x: selfX, y: selfY, moving: false, facing: null };
    const dx = target.x - selfX, dy = target.y - selfY;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.25) return { x: target.x, y: target.y, moving: false, facing: null };
    const maxStep = (this.opts.speed * dtMs) / 1000;
    const step = Math.min(dist, maxStep);
    const nx = selfX + (dx / dist) * step, ny = selfY + (dy / dist) * step;
    const facing: Facing = Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    return { x: nx, y: ny, moving: true, facing };
  }
}
